import React, { useState, useEffect, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Stack,
  Typography,
  Tab,
  Tabs,
  IconButton,
  Chip,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import toast from "react-hot-toast";
import Header from "../components/Header";
import MatchLineUp from "../components/MatchLineUp";
import GameTimeTab from "../components/GameTimeTab";
import Spotlight from "../components/Spotlight";
import MatchSummary from "../components/MatchSummary";
import { getMatchById } from "../config/mainRequest";

const shortName = (name) =>
  (name || "")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase() || "?";

const MatchDetails = () => {
  const { matchId } = useParams();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(0);
  const token = useSelector((state) => state.auth.token);
  const allMatches = useSelector((state) => state.match.all_matches);

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  // Only allow match ids that belong to this analyst's homepage list.
  // null = list not loaded yet (can't validate); true/false = decided.
  const isAllowedMatch = useMemo(() => {
    if (!Array.isArray(allMatches)) return null;
    return allMatches.some(
      (m) => String(m?.id) === String(matchId) || String(m?.match_id) === String(matchId)
    );
  }, [allMatches, matchId]);

  useEffect(() => {
    if (isAllowedMatch === false) {
      toast.error("Match not available in your list");
      history.replace("/home");
    }
  }, [isAllowedMatch, history]);

  // ── Organizer (preselected from match.organizer_id) ──
  const organizers = useMemo(() => match?.organizer_details ?? [], [match]);

  // Fetch the real match details by id (teams, status, organizer, tournament).
  useEffect(() => {
    if (!matchId || !token || isAllowedMatch === false) return;
    let cancelled = false;
    setLoading(true);
    getMatchById({ match_id: matchId, token })
      .then((data) => {
        if (!cancelled) setMatch(data);
      })
      .catch((err) => console.error("[MatchDetails] match fetch failed:", err?.message))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [matchId, token, isAllowedMatch]);

  const organizerId = match?.organizer_id ?? null;
  const selectedOrg =
    organizers.find((o) => o.id === organizerId) || null;

  const matchInfo = {
    id: matchId,
    pdfDetails: match?.pdf_details ?? null,
    status: match?.status ?? null,
    gameTime: match?.game_time ?? null,
    organizerId,
    organizerName: selectedOrg?.name ?? null,
    tournamentId:
      match?.tournament_details?.id ?? match?.tournament_id ?? match?.tournament?.id ?? null,
    startingLineupCount: match?.starting_lineup_count ?? 11,
    teamA: {
      id: match?.team_a_details?.id ?? null,
      name: match?.team_a_details?.name ?? "Team A",
      short: shortName(match?.team_a_details?.name) || "TA",
    },
    teamB: {
      id: match?.team_b_details?.id ?? null,
      name: match?.team_b_details?.name ?? "Team B",
      short: shortName(match?.team_b_details?.name) || "TB",
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100vh",
        backgroundColor: "var(--app-bg)",
        overflow: "hidden",
      }}
    >
      <Header tab="home" />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {/* Combined sub-header + tabs */}
        <Stack
          direction="row"
          alignItems="center"
          flexWrap={{ xs: "wrap", sm: "nowrap" }}
          sx={{
            px: { xs: 1.5, md: 3 },
            py: { xs: 0.5, sm: 0 },
            borderBottom: "1px solid var(--border-1)",
            backgroundColor: "var(--surface-1)",
            columnGap: 1.5,
            rowGap: 0.5,
            minHeight: 44,
          }}
        >
          <IconButton
            size="small"
            onClick={() => (history.length > 1 ? history.goBack() : history.push("/home"))}
            sx={{
              color: "var(--text-2)",
              borderRadius: "6px",
              width: 30,
              height: 30,
              "&:hover": { backgroundColor: "var(--surface-2)" },
            }}
          >
            <ArrowBack sx={{ fontSize: 16 }} />
          </IconButton>

          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 700,
              color: "var(--text-1)",
              whiteSpace: "nowrap",
            }}
          >
            #{matchId}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "var(--text-2)",
              whiteSpace: "nowrap",
              display: { xs: "none", sm: "block" },
              mr: 1,
            }}
          >
            {matchInfo.teamA.name} vs {matchInfo.teamB.name}
          </Typography>

          {selectedOrg && (
            <Chip
              size="small"
              avatar={<Avatar src={selectedOrg.logo_url} alt={selectedOrg.name}>{(selectedOrg.name || "?")[0]}</Avatar>}
              label={
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedOrg.name}
                </span>
              }
              sx={{
                height: 26,
                maxWidth: { xs: 150, sm: 220 },
                fontSize: "11px",
                color: "var(--text-1)",
                backgroundColor: "var(--surface-2)",
                border: "1px solid var(--border-1)",
                cursor: "default",
                "& .MuiChip-label": { color: "var(--text-1)", overflow: "hidden" },
              }}
            />
          )}

          <Box sx={{ height: 24, width: "1px", backgroundColor: "var(--border-1)", mx: 0.5, display: { xs: "none", sm: "block" } }} />

          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 44,
              width: { xs: "100%", sm: "auto" },
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 600,
                minHeight: 44,
                px: 1.5,
                minWidth: "auto",
                color: "var(--text-2)",
                "&.Mui-selected": {
                  color: "rgba(232, 108, 0, 1)",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "rgba(232, 108, 0, 1)",
                height: 2,
              },
            }}
          >
            <Tab label="Match Line Up" />
            <Tab label="Game Time" />
            <Tab label="Spotlight" />
            <Tab label="Summary" />
          </Tabs>
        </Stack>

        {/* Tab Content — Summary owns its scrolling (each section scrolls
            independently), other tabs keep the page-level scroll. */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: activeTab === 3 ? "hidden" : "auto" }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", py: 6 }}>
              <CircularProgress size={32} sx={{ color: "rgba(232,108,0,1)" }} />
            </Stack>
          ) : (
            <>
              {activeTab === 0 && (
                <MatchLineUp matchId={matchId} matchInfo={matchInfo} token={token} />
              )}
              {activeTab === 1 && (
                <GameTimeTab matchId={matchId} matchInfo={matchInfo} token={token} />
              )}
              {activeTab === 2 && (
                <Spotlight matchId={matchId} match={match} token={token} organizerId={organizerId} />
              )}
              {activeTab === 3 && (
                <MatchSummary matchId={matchId} match={match} token={token} organizerId={organizerId} />
              )}
            </>
          )}
        </Box>
      </Box>

    </Box>
  );
};

export default MatchDetails;
